import React, { useContext } from "react";
import { CustomRowModifier, CustomDeleteKey, CustomRowModifierWithRange, CellWarning } from "../../models/CustomRowAction";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import { RowTooltipConfig } from "../../models/table/RowTooltipConfig";
import { TableColumn } from "../../models/table/TableColumn";
import { TableData } from "../../models/table/TableData";
import { TableRowGroup } from "../../models/table/TableRowGroup";
import { ContextToggle } from "./ContextToggle";

export interface TableContextValueType extends ContextToggle {
    tableDescriptor: TableColumn[];
    isMultiSelect: boolean;
    updateTableDescriptor: (newTableDescriptor) => void;
    tableData: TableData[];
    groupedTableData?: TableRowGroup[];
    deleteItem?: (id: string, isCustomDeleteKey?: boolean) => void,
    exportData: (multiSelectOption: MultiSelectOption, title: string, description: string) => void;
    editItem?: (id: string) => void;
    swapItem?: (item: any) => void; //Main used for replacing controller
    customDeleteKey?: CustomDeleteKey; //All our delete method perform delete on id attribute. In some cases we might not need to delete on id so use this e.g. Delete face,finder,hand
    alwaysSelectedAndDisabled?: string[];
    downloadItem?:(item: any) => void;
    busy: boolean;
    customDelete?: CustomRowModifier;
    customDeleteWithRange?: CustomRowModifierWithRange;
    customEdit?: CustomRowModifier;
    customEditWithRange?: CustomRowModifierWithRange;
    customDownload?: CustomRowModifier;
    canExportReport?: boolean;
    manuallyMuster?: (rowData: any) => void;
    customMusteringWithRange?: CustomRowModifierWithRange;
    menuId?: string;
    onShowWarning?: (warningMessage: CellWarning) => void;
    onMoveUpOrDown?: (item: any, rowIndex: number, direction: 'up' | 'down') => void;
    setRequestToLoadDataOnDemand: React.Dispatch<React.SetStateAction<boolean>>;
    loadDataOnDemand: boolean;
    emptyStateMessage?: string;
    onAddNew?: () => void;
    showAddNewButton?: boolean;
    runDataOnDemandButtonLabel?: string;
    rowTooltipConfig?: RowTooltipConfig;
    childColumns?: TableColumn[];
    downloadGroup?: (parentRow: any, childRows: any[], format: string) => void;
    syncCollapsedGroups?: (groups: Record<string, boolean>) => void;
    exportVisibleData?: (format: string) => void;
    setReportTitle?: (title: string) => void;
}

export const emptyTableContextValue: TableContextValueType = {
    isInUse: false,
    tableDescriptor: new Array(0),
    isMultiSelect: false,
    updateTableDescriptor: (newTableDescriptor) => { },
    tableData: new Array(0),
    groupedTableData: new Array(0),
    deleteItem: undefined,
    exportData: () => {},
    editItem: undefined,
    swapItem: undefined,
    customDeleteKey: undefined,
    alwaysSelectedAndDisabled: undefined,
    busy: false,
    customDelete: undefined,
    customDeleteWithRange: undefined,
    customDownload: undefined,
    customEdit: undefined,
    customEditWithRange: undefined,
    canExportReport: true,
    manuallyMuster: undefined,
    customMusteringWithRange: undefined,
    menuId: undefined,
    onShowWarning: undefined,
    onMoveUpOrDown: undefined,
    setRequestToLoadDataOnDemand: () => {},
    loadDataOnDemand: false,
    emptyStateMessage: undefined,
    onAddNew: undefined,
    showAddNewButton: undefined,
    runDataOnDemandButtonLabel: undefined,
    rowTooltipConfig: undefined,
    childColumns: undefined,
    downloadGroup: undefined,
    syncCollapsedGroups: undefined,
    exportVisibleData: undefined,
    setReportTitle: undefined,
}

export const useTable = () => {
    return useContext(TableContext);
}

export const TableContext = React.createContext<TableContextValueType>(emptyTableContextValue);
